import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, EnvelopeSimple, Globe, ShieldCheck, Buildings } from '@phosphor-icons/react';

export function Privacy() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={18} className="mr-1" /> Back
              </Button>
            </Link>
            <h1 className="text-xl font-heading flex items-center gap-2">
              <ShieldCheck size={24} weight="duotone" className="text-primary" />
              PRIVACY POLICY
            </h1>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="prose prose-invert max-w-none">
          {/* Header Info */}
          <div className="mb-8 p-6 bg-card border border-border rounded-lg">
            <h2 className="text-2xl font-heading mt-0 mb-2">Stay On Hoops</h2>
            <p className="text-sm text-primary font-medium mb-4">A Product of Intuitive Mentors Organization</p>
            <div className="flex flex-col sm:flex-row gap-4 text-sm text-muted-foreground">
              <a href="https://www.stayonhoops.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Globe size={16} /> www.stayonhoops.com
              </a>
              <a href="mailto:info@stayonhoops.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                <EnvelopeSimple size={16} /> info@stayonhoops.com
              </a>
            </div>
            <p className="mt-4 text-sm text-muted-foreground mb-0">
              <strong>Effective Date:</strong> February 1, 2026
            </p>
          </div>
          
          {/* Section 1 - WHO WE ARE (New) */}
          <section className="mb-8 p-6 bg-primary/5 border border-primary/20 rounded-lg">
            <h3 className="text-lg font-heading text-primary flex items-center gap-2 mt-0">
              <Buildings size={20} weight="duotone" />
              1. WHO WE ARE
            </h3>
            <p>
              Stay On Hoops is a youth basketball program and online platform operated by <strong>Intuitive Mentors Organization</strong>.
            </p>
            <p className="mb-0">
              This Privacy Policy applies to information collected through www.stayonhoops.com and any related services operated by Intuitive Mentors Organization under the Stay On Hoops brand.
            </p>
          </section>
          
          {/* Section 2 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">2. OUR COMMITMENT</h3>
            <p>
              Intuitive Mentors Organization, through Stay On Hoops, respects your privacy and is committed to protecting the personal information of parents and children.
            </p>
            <p>We comply with:</p>
            <ul>
              <li><strong>COPPA</strong> (Children&apos;s Online Privacy Protection Act)</li>
              <li><strong>California Consumer Privacy Act (CCPA/CPRA)</strong></li>
            </ul>
          </section>
          
          {/* Section 3 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">3. INFORMATION WE COLLECT</h3>
            
            <h4 className="text-md font-medium mt-4 mb-2">Parent / Guardian Information</h4>
            <ul>
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
            </ul>
            
            <h4 className="text-md font-medium mt-4 mb-2">Child (Participant) Information</h4>
            <ul>
              <li>Name</li>
              <li>Date of birth</li>
              <li>Age</li>
              <li>Emergency contact information</li>
              <li>Medical notes (optional)</li>
            </ul>
            
            <h4 className="text-md font-medium mt-4 mb-2">Technical Information</h4>
            <ul>
              <li>IP address</li>
              <li>Browser and device data</li>
              <li>Log and usage data</li>
            </ul>
          </section>
          
          {/* Section 4 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">4. HOW WE USE INFORMATION</h3>
            <p>We use collected information to:</p>
            <ul>
              <li>Register participants</li>
              <li>Verify waivers</li>
              <li>Manage sessions and attendance</li>
              <li>Communicate reminders and updates</li>
              <li>Process payments</li>
              <li>Maintain safety and records</li>
            </ul>
            <p className="font-medium text-primary">
              We do not use child data for advertising.
            </p>
          </section>
          
          {/* Section 5 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">5. CHILDREN&apos;S PRIVACY (COPPA)</h3>
            <ul>
              <li>We collect child information only with parental consent</li>
              <li>Parents may review, update, or delete their child&apos;s data</li>
              <li>We do not knowingly collect information from children without parental involvement</li>
            </ul>
            <p>
              Requests can be sent to{' '}
              <a href="mailto:info@stayonhoops.com" className="text-primary hover:underline">info@stayonhoops.com</a>.
            </p>
          </section>
          
          {/* Section 6 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">6. PAYMENT DATA</h3>
            <p>
              All payments are processed by <strong>Stripe</strong>.
            </p>
            <p className="font-medium">
              Neither Intuitive Mentors Organization nor Stay On Hoops stores credit card information.
            </p>
          </section>
          
          {/* Section 7 - Updated */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">7. DATA SHARING</h3>
            <p className="font-medium text-green-500">
              We do not sell or rent personal data.
            </p>
            <p>Data may be shared only with:</p>
            <ul>
              <li>Payment processors (Stripe)</li>
              <li>Email/notification services</li>
              <li>Legal authorities if required</li>
            </ul>
            <p>
              Information may be shared internally within Intuitive Mentors Organization solely for administrative, safety, and operational purposes related to Stay On Hoops.
            </p>
          </section>
          
          {/* Section 8 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">8. CALIFORNIA PRIVACY RIGHTS (CCPA/CPRA)</h3>
            <p>California residents have the right to:</p>
            <ul>
              <li>Know what personal data we collect</li>
              <li>Request deletion of personal data</li>
              <li>Correct inaccurate information</li>
              <li>Opt out of data sale (we do not sell data)</li>
            </ul>
            <p>
              Requests may be made by emailing{' '}
              <a href="mailto:info@stayonhoops.com" className="text-primary hover:underline">info@stayonhoops.com</a>.
            </p>
          </section>
          
          {/* Section 9 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">9. DATA SECURITY</h3>
            <p>
              We implement reasonable safeguards to protect personal data. No system is completely secure, but we take privacy seriously.
            </p>
          </section>
          
          {/* Section 10 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">10. DATA RETENTION</h3>
            <p>Information is retained only as long as necessary for:</p>
            <ul>
              <li>Program operations</li>
              <li>Legal compliance</li>
              <li>Safety and recordkeeping</li>
            </ul>
          </section>
          
          {/* Section 11 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">11. POLICY UPDATES</h3>
            <p>
              We may update this Privacy Policy periodically. Changes will be posted on www.stayonhoops.com.
            </p>
          </section>
          
          {/* Section 12 - Updated Contact */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">12. CONTACT</h3>
            <p>
              <a href="mailto:info@stayonhoops.com" className="text-primary hover:underline flex items-center gap-2">
                <EnvelopeSimple size={18} /> info@stayonhoops.com
              </a>
            </p>
            <p className="text-sm text-muted-foreground">
              Operated by Intuitive Mentors Organization
            </p>
          </section>
        </div>
        
        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">
            Terms & Conditions
          </Link>
          <span className="hidden sm:inline text-muted-foreground">•</span>
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
